<template>
  <!-- eslint-disable vue/no-lone-template -->
  <div class="user-list">
    <header style="font-weight: bold">
      2. ลูกบ้าน
      <span v-if="value.length">({{ numberOfUser }} คน)</span>
    </header>
    <hr />
    <div class="row">
      <el-autocomplete
        v-model="search"
        :fetch-suggestions="searchSuggestions"
        :trigger-on-focus="false"
        :disabled="canSelectJustOneUser || !transferId"
        class="user-search-input col-6"
        placeholder="ค้นหาลูกบ้าน"
        @select="handleSelect"
      >
        <template slot-scope="{ item }">
          <div class="user">
            <div class="name">{{ item.name }}</div>
            <div class="home">{{ item.home }}</div>
            <div class="home">{{ item.role }}</div>
          </div>
        </template>
      </el-autocomplete>
      <el-autocomplete
        v-model="project"
        :fetch-suggestions="projectSuggestions"
        :trigger-on-focus="false"
        :disabled="canSelectJustOneUser || !transferId"
        class="user-search-input col-6"
        placeholder="ค้นหาโครงการ / บ้านเลขที่"
        @select="handleSelect"
      >
        <template slot-scope="{ item }">
          <div class="user">
            <div class="name">{{ item.name }}</div>
            <div class="home">{{ item.home }}</div>
            <div class="home">{{ item.role }}</div>
          </div>
        </template>
      </el-autocomplete>
    </div>
    <div v-for="user in value" :key="userKey(user)" class="user-list-item">
      <div class="user">
        <div class="name">{{ user.name }}</div>
        <div class="home">{{ user.home }}</div>
        <div class="home">{{ user.role }}</div>
      </div>
      <button
        class="btn-delete"
        @click="handleClickDeleteUser(user.id, user.homeId)"
      >
        <img :alt="$t('label.delete')" src="/static/icons/trash.svg" />
      </button>
    </div>
  </div>
</template>

<script>
import { baanRueJaiClient } from "@/dependencies"
import { toCamelCaseObject } from "@/lib/util"
import { Autocomplete } from "element-ui"

export default {
  components: {
    ElAutocomplete: Autocomplete,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    canSelectOneUser: {
      type: Boolean,
      default: false,
    },
    isFilterTokenxUser: {
      type: Boolean,
      default: false,
    },
    transferId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      search: "",
      project: "",
      searchSuggestions: () => {},
      projectSuggestions: () => {},
      latestRequestIdByChannel: {
        search: 0,
        project: 0,
      },
    }
  },
  computed: {
    numberOfUser() {
      return this.value.length
    },
    canSelectJustOneUser() {
      return this.canSelectOneUser && this.value.length === 1
    },
  },
  created() {
    this.searchSuggestions = this.createUserSearchHandler("search")
    this.projectSuggestions = this.createUserSearchHandler("project")
  },
  methods: {
    createUserSearchHandler(channel) {
      return (queryString, cb) => {
        const paramsByChannel = {
          search: {
            textSearch: queryString,
            projectHomeSearch: "",
          },
          project: {
            textSearch: "",
            projectHomeSearch: queryString,
          },
        }

        return this.runUserSearch({
          channel,
          ...paramsByChannel[channel],
          cb,
        })
      }
    },
    getNextRequestId(channel) {
      const nextRequestId = this.latestRequestIdByChannel[channel] + 1

      this.latestRequestIdByChannel = {
        ...this.latestRequestIdByChannel,
        [channel]: nextRequestId,
      }

      return nextRequestId
    },
    isStaleRequest(channel, requestId) {
      return requestId !== this.latestRequestIdByChannel[channel]
    },
    async runUserSearch({ channel, textSearch, projectHomeSearch, cb }) {
      const requestId = this.getNextRequestId(channel)

      try {
        const query = this.buildSearchQuery({
          textSearch,
          projectHomeSearch,
        })
        const response = await baanRueJaiClient.admin.users.search(query)

        if (this.isStaleRequest(channel, requestId)) {
          return
        }

        const { users } = response.data.result
        const normalizedUsers = this.normalizeUsers(users)

        if (this.isFilterTokenxUser) {
          cb(this.mapUsersWithFirstHome(normalizedUsers))

          return
        }

        cb(this.mapUsersWithAllHomes(normalizedUsers))
      } catch (error) {
        if (this.isStaleRequest(channel, requestId)) {
          return
        }

        cb([])
      }
    },
    userKey(user) {
      return `${user.id}-${user.homeId}`
    },
    getHomeRole(home) {
      return home.userRole || home.role
    },
    buildSearchQuery({ textSearch, projectHomeSearch }) {
      let query = {
        text_search: textSearch,
        project_home_search: projectHomeSearch,
      }

      if (this.isFilterTokenxUser) {
        query = { ...query, is_filter_tokenx_user: true }
      }

      return query
    },
    normalizeUsers(users) {
      return users
        .map((user) => toCamelCaseObject(user))
        .filter((user) => user.homes && user.homes.length)
    },
    mapUsersWithFirstHome(users) {
      return users.map((item) => {
        const { user } = item
        const name = `${user.userProfile.firstName} ${user.userProfile.lastName}`
        const home = item.homes[0]

        return {
          id: user.userId,
          homeId: home.homeId,
          projectId: home.projectId,
          name,
          home: `${home.addressNo} ${home.projectName}`,
          role: this.getHomeRole(home),
          homes: item.homes,
        }
      })
    },
    mapUsersWithAllHomes(users) {
      return users.flatMap((item) => {
        const { user } = item
        const { homes } = item
        const name = `${user.userProfile.firstName} ${user.userProfile.lastName}`

        return homes.map((home) => ({
          id: user.userId,
          homeId: home.homeId,
          projectId: home.projectId,
          name,
          home: `${home.addressNo} ${home.projectName}`,
          role: this.getHomeRole(home),
          data: { user, home },
        }))
      })
    },
    handleSelect(item) {
      const isDuplicate = this.value.some(
        (user) => user.id === item.id && user.homeId === item.homeId,
      )

      if (isDuplicate) {
        return
      }

      this.$emit("input", [...this.value, item])
      this.search = ""
      this.project = ""
    },
    handleClickDeleteUser(userId, homeId) {
      const filteredUsers = this.value.filter(
        (user) => !(user.id === userId && user.homeId === homeId),
      )

      this.$emit("input", filteredUsers)
    },
  },
}
</script>

<style lang="scss" scoped>
.user {
  padding: 6px 0;

  > .name {
    font-weight: bold;
  }

  > .name,
  > .home {
    line-height: 1.4;
  }

  > .home {
    color: #777;
    font-size: 12px;
    white-space: normal;
    word-break: break-word;
  }
}

.user-search-input {
  margin-bottom: 10px;
  width: 100%;
}

.btn-delete {
  width: 36px;
  height: 36px;
  border-radius: 36px;
  padding: 0;
}

.user-list-item {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: 8px 0;

  > .user {
    flex-grow: 1;
    padding: 0;
  }
}
</style>
